import React from 'react';
import styled, { StylesProps } from '../../../../../lib/styles';
import { compose } from 'recompose';
import Modal from 'react-modal';
import clsx from 'clsx';

import theme from '../../../../../lib/theme';
import Spacer from '../../../../../components/presentational/Spacer';
import Button from '../../../../../components/presentational/Button';
import Icon, { Icons } from '../../../../../components/presentational/Icon';

import membershipDealBlockStyles from './MemberDealBlock_styles';
import lodash from 'lodash';
import { useMountEffect } from '../../../../../lib/hooks';
import { TRIGGER_MEMBERDEAL, raiseEvent } from '../../../../../lib/gtm';

const {
  media,
} = theme;

export interface MemberDealBlockData {
  type: 'member_deal_block';
  title: string;
  description: string;
  showGroups: boolean;
  deals: MemberDeal[];
}

interface MemberDeal {
  id: string;
  title: string;
  teaser: string;
  excerpt: string;
  description: string;
  memberImage: string;
  images: {
    image: string;
    mobileImage: string;
  }
  memberPhone: string,
  ctaHeading: string,
  ctaExcerpt: string,
  ctaDescription: string,
  ctaButtonText: string,
  termsConditions: string,
  termsConditionsLink: string,
  dealCategory: DealTaxonomy[],
  dealCompany: DealTaxonomy,
  dealType: DealTaxonomy[],
  selectedPost: boolean,
}

interface DealTaxonomy {
  id: number;
  name: string;
  icon?: Icons;
}

export interface MembershipDealBlockProps {
  block: MemberDealBlockData;
  blockIndex: number;
}

const modalStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    zIndex: 9999,
  },
  content: {
    position: `relative`,
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    padding: 0,
    width: '95%',
    borderWidth: '0',
    outline: '0',

    [media.l.up]: {
      width: '70% !important',
    },

  },
};

export const MembershipDealBlock = ({
  block,
  styles,
}: MembershipDealBlockProps & StylesProps<typeof membershipDealBlockStyles>) => {
  const [modalIsOpen, setIsOpen] = React.useState(false);
  const [selectedDeal, setSelectedDeal] = React.useState<MemberDeal | undefined>(block.deals.filter(d => d.selectedPost)[0]);
  const [activeModalTab, setActiveModalTab] = React.useState<'details' | 'disclosures'>('details');
  
  useMountEffect(() => {
    setIsOpen(!!selectedDeal);
  });

  const dealCategories = lodash.sortBy(lodash.uniqBy(lodash.flatten(block.deals.map(deal => deal.dealCategory)), 'name'), 'name'); 
  const dealsByCategoryId = (categoryId: number) => {
    return block.deals.filter(deal => deal.dealCategory.some(category => category.id === categoryId));
  };

  const dealTypes = lodash.sortBy(lodash.uniqBy(lodash.flatten(block.deals.map(deal => deal.dealType)), 'name'), 'name'); 
  const dealsByTypeId = (typeId: number) => {
    return block.deals.filter(deal => deal.dealType.some(type => type.id === typeId));
  };

  const renderDeal = (deal: MemberDeal, d: number) => (
    <div key={d} className={`dealContainer ${styles.dealContainer}`}>
      {deal.memberImage && (
      <div className={`dealImage ${styles.dealImage}`}>
        <img src={deal.memberImage} alt="" />
      </div>
      )}
      <div className={`m-0`}>
        <h3
          onClick={() => {
            setSelectedDeal(deal);
            setIsOpen(true);
            raiseEvent({
              event: TRIGGER_MEMBERDEAL,
              sasEventType: `MEMBERDEAL_MODAL_OPENED: ${deal.title}`,
            });
        }}>
          {deal.title}
        </h3>
        <div className={`dealExcerpt ${styles.dealExcerpt}`}
          dangerouslySetInnerHTML={{__html: deal.excerpt}}></div>
        <div className={`dealCompany ${styles.dealCompany}`}>
          {deal.dealCompany.name}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className={`memberdeals-block-container ${styles.container}`}>
        <Spacer width={1160} space={['m', 'l']}>
          {block.title && (
            <h2 className={styles.blockHeading}>{block.title}</h2>
          )}

          {block.description && (
            <p className={styles.blockDescription}
              dangerouslySetInnerHTML={{__html: block.description}}></p>
          )}

          <div className={`dealsContainer ${styles.dealsContainer}`}>
            {!!block.showGroups && (
              <>
              {dealsByTypeId.length > 0 && dealTypes.map((type, t) => (
                <div key={t} className={`memberDealCategoryContainer ${styles.memberDealCategoryContainer}`}>
                  <h2 className={`categoryHeading ${styles.categoryHeading}`}>
                    <Icon icon={type.icon ?? 'verified'} size={'xxl'} rotateIcon={false} className={'mr-2'} />
                    {type.name}
                  </h2>
                  <div className={`categoryDealsContainer ${styles.categoryDealsContainer}`}>
                  {dealsByTypeId(type.id).map((deal, d) => renderDeal(deal, d))}
                  </div>
                </div>
              ))}
              {dealCategories.map((category, c) => (
                <div key={c} className={`memberDealCategoryContainer ${styles.memberDealCategoryContainer}`}>
                  <h2 className={`categoryHeading ${styles.categoryHeading}`}>
                    <Icon icon={category.icon ?? 'verified'} size={'xxl'} rotateIcon={false} className={'mr-2'} />
                    {category.name}
                  </h2>
                  <div className={`categoryDealsContainer ${styles.categoryDealsContainer}`}>
                  {dealsByCategoryId(category.id).map((deal, d) => renderDeal(deal, d))}
                  </div>
                </div>
              ))}
              </>
            )}

            {!block.showGroups && (
              <div className={`memberDealCategoryContainer ${styles.memberDealCategoryContainer}`}>
                <div className={`categoryDealsContainer ${styles.categoryDealsContainer}`} style={{borderTopWidth: `0`}}>
                  {block.deals.map((deal, d) => renderDeal(deal, d))}
                </div>
              </div>
            )}
          </div>
        </Spacer>
      </div>
    
      {selectedDeal && (
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={() => setIsOpen(false)}
        contentLabel={selectedDeal.title}
        style={modalStyles}
        className={`dealModal`}
      >
        <div className={`dealModalTitleContainer ${styles.dealModalTitleContainer}`}>
          <div className={`modalTabs ${styles.modalTabs}`}>
            <div 
              className={clsx(`tab ${styles.tab}`, {
                [styles.activeTab]: activeModalTab == 'details'
              })}
              onClick={() => {
                setActiveModalTab('details');
              }}
            >
              Details
            </div>
            <div 
              className={clsx(`tab ${styles.tab}`, {
                [styles.activeTab]: activeModalTab == 'disclosures'
              })}
              onClick={() => {
                setActiveModalTab('disclosures');
              }}
            >
              Terms &amp; Conditions
            </div>
          </div>
          <Icon className={`modalCloseIcon ${styles.modalCloseIcon}`} icon={'close'} onClick={() => setIsOpen(false)} />
        </div>
        <div className={`dealModalContentContainer ${styles.dealModalContentContainer}`}>
          <div className={`contentLeft ${styles.contentLeft} ${styles.content}`}>
            {activeModalTab == 'details' && (
              <>
                {selectedDeal.teaser && (
                  <span className={`modalDealTeaser ${styles.modalDealTeaser}`}>{selectedDeal.teaser}</span>
                )}
                <h2 className={`modalDealTitle ${styles.modalDealTitle}`}>{selectedDeal.title}</h2>
                {selectedDeal.description && (
                  <p className={`modalDealDescription ${styles.modalDealDescription}`}
                    dangerouslySetInnerHTML={{__html: selectedDeal.description}}></p>
                )}
                {selectedDeal.images.image && (
                  <img className={`modalDealImage ${styles.modalDealImage}`} src={selectedDeal.images.image} alt={selectedDeal.title} />
                )}                
              </>
            )}
            {activeModalTab == 'disclosures' && (
              <p dangerouslySetInnerHTML={{__html: selectedDeal.termsConditions}}></p>
            )}
          </div>
          <div className={`contentRight ${styles.contentRight} ${styles.content}`}>
            <div className={`ctaHeading ${styles.ctaHeading}`}
              dangerouslySetInnerHTML={{__html: selectedDeal.ctaHeading}}></div>
            <div className={`ctaExcerpt ${styles.ctaExcerpt}`}
              dangerouslySetInnerHTML={{__html: selectedDeal.ctaExcerpt}}></div>
            <div className={`ctaDescription ${styles.ctaDescription}`}>
              <p dangerouslySetInnerHTML={{__html: selectedDeal.ctaDescription}}></p>
              <Button 
                className={`ctaButton ${styles.ctaButton}`}
                href={selectedDeal.termsConditionsLink}
                onClick={() => {
                  raiseEvent({
                    event: TRIGGER_MEMBERDEAL,
                    sasEventType: `MEMBERDEAL_LINK_CLICKED: ${selectedDeal.title}`,
                  });
                }}
              >
                {selectedDeal.ctaButtonText}
              </Button>
            </div>
            {selectedDeal.memberPhone && (
              <div className={`memberPhone ${styles.memberPhone}`}>
                or call <a href={`tel:${selectedDeal.memberPhone}`}>{selectedDeal.memberPhone}</a>
              </div>
            )}
          </div>
        </div>
      </Modal>
      )}
    </>
  );
};

export default compose(styled(membershipDealBlockStyles))(MembershipDealBlock);
